/**
 * Funções auxiliares para o módulo Oferta Formulário
 */

import envConfig from '../../config/env.js';

function toKebabCase(value) {
    if (!value) return '';

    return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

/**
 * Normaliza dados da oferta mapeando campos do backend
 */
export function normalizeOfertaData(oferta) {
    if (!oferta) return null;

    const normalized = { ...oferta };

    // Mapeia opt_in_widget (backend) para optin_widget (frontend)
    if (oferta.opt_in_widget !== undefined && normalized.optin_widget === undefined) {
        normalized.optin_widget = oferta.opt_in_widget;
    }

    // Mapeia campos com nomes diferentes
    if (oferta.cpf !== undefined && oferta.hasCpf === undefined) {
        normalized.hasCpf = oferta.cpf;
    }

    if (oferta.cep !== undefined && oferta.zipCode === undefined) {
        normalized.zipCode = oferta.cep;
    }

    if (oferta.birthdate !== undefined && oferta.birthDate === undefined) {
        normalized.birthDate = oferta.birthdate;
    }

    // Mapeia isOfferLP (oferta crua do GraphQL, ex: notícias via API standalone)
    // para isOfferlp (formato do controller widget-modular do Strapi)
    if (oferta.isOfferLP !== undefined && oferta.isOfferlp === undefined) {
        normalized.isOfferlp = oferta.isOfferLP;
    }

    // Mapeia campos de texto dos checkboxes
    if (oferta.text_aceite && !normalized.aceptText) {
        normalized.aceptText = oferta.text_aceite;
    }

    if (oferta.text_estou_ciente && !normalized.textAware) {
        normalized.textAware = oferta.text_estou_ciente;
    }

    if (oferta.text_declaro && !normalized.textDeclare) {
        normalized.textDeclare = oferta.text_declaro;
    }

    // Mapeia campaign_identifier para hash
    if (oferta.campaign_identifier && !normalized.hash) {
        normalized.hash = oferta.campaign_identifier;
    }

    // Mapeia possíveis variações de slug da oferta
    if (!normalized.slug) {
        normalized.slug = oferta.slug ||
            oferta.Slug ||
            oferta.offer_slug ||
            oferta.offerSlug ||
            oferta.slug_oferta ||
            oferta.slugOferta ||
            '';
    }

    // Fallback final: gera slug a partir do título da oferta
    if (!normalized.slug) {
        normalized.slug = toKebabCase(
            oferta.title ||
            oferta.offer_title ||
            oferta.titulo ||
            ''
        );
    }

    return normalized;
}

/**
 * Ordem de prioridade canônica para offer_id/campaign_identifier nos eventos GA
 * (SPEC.md > SDD "padronizacao-identificador-oferta-ga" §4.1): ofertaData.hash é
 * a fonte primária, por ser o mesmo campo (mapeado de campaign_identifier) usado
 * como campaignIdentifier no envio ao Motor de Leads (sendFormData, fora de
 * escopo deste ciclo). Única função usada por form_start/generate_lead/lead/
 * position (via getWidgetInstanceId) nos 3 handlers de oferta.
 */
export function resolveOfferId(ofertaData, config) {
    if (isNonEmpty(ofertaData?.hash)) return ofertaData.hash;
    if (isNonEmpty(ofertaData?.documentId)) return ofertaData.documentId;
    if (isNonEmpty(config?.id)) return config.id;
    return 'sem-id';
}

function isNonEmpty(value) {
    return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Verifica se oferta está em rascunho
 */
export function isDraftOffer(ofertaData, config) {
    return ofertaData?.is_draft ||
        ofertaData?.isDraft ||
        config.is_draft ||
        config.isDraft ||
        false;
}

/**
 * Obtém URL da imagem da oferta
 */
export function getOfferImageUrl(ofertaData) {
    if (!ofertaData) return null;

    let imageUrl = null;

    // 1. Campo 'image' direto (pode ser string ou objeto)
    if (ofertaData.image) {
        if (typeof ofertaData.image === 'string') {
            imageUrl = ofertaData.image;
        } else if (ofertaData.image.url) {
            imageUrl = ofertaData.image.url;
        } else if (ofertaData.image.formats?.large?.url) {
            imageUrl = ofertaData.image.formats.large.url;
        } else if (ofertaData.image.formats?.medium?.url) {
            imageUrl = ofertaData.image.formats.medium.url;
        }
    }

    // 2. Tenta 'featured_image' ou 'featuredImage'
    if (!imageUrl && ofertaData.featured_image) {
        imageUrl = typeof ofertaData.featured_image === 'string'
            ? ofertaData.featured_image
            : ofertaData.featured_image.url || ofertaData.featured_image.formats?.large?.url;
    }

    // 3. Tenta 'cover_image' ou 'coverImage'
    if (!imageUrl && ofertaData.cover_image) {
        imageUrl = typeof ofertaData.cover_image === 'string'
            ? ofertaData.cover_image
            : ofertaData.cover_image.url || ofertaData.cover_image.formats?.large?.url;
    }

    // 4. Tenta array de imagens (pega a primeira)
    if (!imageUrl && Array.isArray(ofertaData.images) && ofertaData.images.length > 0) {
        const firstImage = ofertaData.images[0];
        imageUrl = typeof firstImage === 'string'
            ? firstImage
            : firstImage.url || firstImage.formats?.large?.url;
    }

    // 5. Se a URL não começar com http/https, adiciona base URL do Strapi
    if (imageUrl && !imageUrl.startsWith('http')) {
        const strapiUrl = envConfig.get('STRAPI_API_URL') || 'http://localhost:1337';
        imageUrl = `${strapiUrl}${imageUrl}`;
    }

    return imageUrl;
}

/**
 * Obtém título da oferta de múltiplos campos
 */
export function getOfferTitle(ofertaData) {
    return ofertaData?.offer_title ||
        ofertaData?.title ||
        ofertaData?.titulo ||
        ofertaData?.name ||
        '';
}

/**
 * Obtém descrição da oferta de múltiplos campos
 */
export function getOfferDescription(ofertaData) {
    return ofertaData?.marketingHook ||
        ofertaData?.text_call ||
        ofertaData?.description ||
        ofertaData?.descricao ||
        '';
}

/**
 * Escapa HTML para segurança
 */
export function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
